// ============================================================================
// MANPOWER SETTINGS MODAL - WITH QUICK ASSIGN
// Enhanced version with one-click employee assignment
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { manpowerService, EmployeeMasterData, ManpowerProject } from '../../../../services/manpower.service';
import styles from './ManpowerSettingsModal.module.css';

interface ManpowerSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEmployeeAssigned?: () => void; // Callback to refresh parent
}

interface Analytics {
  totalInMasterSheet: number;
  totalAssigned: number;
  unassignedEmployees: EmployeeMasterData[];
  duplicateAssignments: {
    employee: EmployeeMasterData;
    projects: string[];
  }[];
}

const ManpowerSettingsModal: React.FC<ManpowerSettingsModalProps> = ({ 
  isOpen, 
  onClose,
  onEmployeeAssigned 
}) => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'unassigned' | 'duplicates'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  // For assignment
  const [projects, setProjects] = useState<ManpowerProject[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<Map<string, string>>(new Map());
  const [assigning, setAssigning] = useState<Set<string>>(new Set());
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadAnalytics();
      loadProjects();
    }
  }, [isOpen]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const data = await manpowerService.getEmployeeAssignmentAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const projectList = await manpowerService.getAllProjects();
      setProjects(projectList);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const handleProjectSelect = (empNumber: string, projectName: string) => {
    const newSelected = new Map(selectedProjects);
    newSelected.set(empNumber, projectName);
    setSelectedProjects(newSelected);
  };

  const handleAssignEmployee = async (employee: EmployeeMasterData) => {
    const projectName = selectedProjects.get(employee.employeeNumber);
    
    if (!projectName) {
      setError('Please select a project first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setAssigning(new Set(assigning).add(employee.employeeNumber));
    setError(null);
    setSuccess(null);

    try {
      await manpowerService.addEmployeeToProject(
        projectName,
        {
          sNo: 0, // Will be reassigned when viewing
          empCode: employee.employeeNumber.replace('EMP', ''),
          empNumber: employee.employeeNumber,
          name: employee.name,
          designation: employee.designation,
          department: employee.department,
          site: 'MAIN', // Default site
          position: employee.designation || 'Staff',
          projectCode: '',
          addedAt: Timestamp.now(),
          addedBy: 'system'
        },
        'system'
      );

      setSuccess(`✅ ${employee.name} assigned to ${projectName}`);
      
      // Refresh analytics
      await loadAnalytics();
      
      // Notify parent to refresh
      if (onEmployeeAssigned) {
        onEmployeeAssigned();
      }

      // Clear selection
      const newSelected = new Map(selectedProjects);
      newSelected.delete(employee.employeeNumber);
      setSelectedProjects(newSelected);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to assign employee: ${err}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      const newAssigning = new Set(assigning);
      newAssigning.delete(employee.employeeNumber);
      setAssigning(newAssigning);
    }
  };

  if (!isOpen) return null;

  const filteredUnassigned = analytics?.unassignedEmployees.filter(emp =>
    emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.employeeNumber.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredDuplicates = analytics?.duplicateAssignments.filter(item =>
    item.employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.employee.employeeNumber.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2>Employee Assignment Analytics</h2>
          <button onClick={onClose} className={styles.closeButton}>×</button>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className={styles.successBanner}>
            {success}
          </div>
        )}
        {error && (
          <div className={styles.errorBanner}>
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={activeTab === 'overview' ? styles.activeTab : styles.tab}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={activeTab === 'unassigned' ? styles.activeTab : styles.tab}
            onClick={() => setActiveTab('unassigned')}
          >
            Unassigned ({analytics?.unassignedEmployees.length || 0})
          </button>
          <button
            className={activeTab === 'duplicates' ? styles.activeTab : styles.tab}
            onClick={() => setActiveTab('duplicates')}
          >
            Duplicates ({analytics?.duplicateAssignments.length || 0})
          </button>
        </div>

        {/* Content */}
        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loading}>Loading analytics...</div>
          ) : (
            <>
              {/* OVERVIEW TAB */}
              {activeTab === 'overview' && analytics && (
                <div className={styles.overview}>
                  <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                      <div className={styles.statValue}>{analytics.totalInMasterSheet}</div>
                      <div className={styles.statLabel}>Total in MasterSheet</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={`${styles.statValue} ${styles.success}`}>
                        {analytics.totalAssigned}
                      </div>
                      <div className={styles.statLabel}>Assigned to Projects</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={`${styles.statValue} ${styles.warning}`}>
                        {analytics.unassignedEmployees.length}
                      </div>
                      <div className={styles.statLabel}>Unassigned</div>
                    </div>
                    <div className={styles.statCard}>
                      <div className={`${styles.statValue} ${styles.danger}`}>
                        {analytics.duplicateAssignments.length}
                      </div>
                      <div className={styles.statLabel}>Duplicate Assignments</div>
                    </div>
                  </div>

                  <div className={styles.summaryText}>
                    {analytics.unassignedEmployees.length > 0 && (
                      <p className={styles.warningText}>
                        ⚠️ {analytics.unassignedEmployees.length} employees are not assigned to any project
                      </p>
                    )}
                    {analytics.duplicateAssignments.length > 0 && (
                      <p className={styles.dangerText}>
                        ⚠️ {analytics.duplicateAssignments.length} employees are assigned to multiple projects
                      </p>
                    )}
                    {analytics.unassignedEmployees.length === 0 && analytics.duplicateAssignments.length === 0 && (
                      <p className={styles.successText}>
                        ✅ All employees are properly assigned!
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* UNASSIGNED TAB WITH QUICK ASSIGN */}
              {activeTab === 'unassigned' && (
                <div className={styles.listView}>
                  <div className={styles.searchBar}>
                    <input
                      type="text"
                      placeholder="Search unassigned employees..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>

                  {filteredUnassigned.length > 0 ? (
                    <div className={styles.employeeList}>
                      {filteredUnassigned.map((emp, index) => (
                        <div key={emp.employeeNumber} className={styles.employeeCard}>
                          <div className={styles.employeeIndex}>{index + 1}</div>
                          <div className={styles.employeeInfo}>
                            <div className={styles.employeeName}>{emp.name}</div>
                            <div className={styles.employeeDetails}>
                              <span className={styles.empCode}>{emp.employeeNumber}</span>
                              {emp.designation && <span className={styles.designation}>{emp.designation}</span>}
                              {emp.department && <span className={styles.department}>{emp.department}</span>}
                            </div>
                          </div>
                          
                          {/* QUICK ASSIGN SECTION */}
                          <div className={styles.assignSection}>
                            <select
                              value={selectedProjects.get(emp.employeeNumber) || ''}
                              onChange={(e) => handleProjectSelect(emp.employeeNumber, e.target.value)}
                              className={styles.projectSelect}
                              disabled={assigning.has(emp.employeeNumber)}
                            >
                              <option value="">Select Project...</option>
                              {projects.map(project => (
                                <option key={project.id} value={project.projectName}>
                                  {project.projectName}
                                </option>
                              ))}
                            </select>
                            
                            <button
                              onClick={() => handleAssignEmployee(emp)}
                              className={styles.assignButton}
                              disabled={!selectedProjects.get(emp.employeeNumber) || assigning.has(emp.employeeNumber)}
                            >
                              {assigning.has(emp.employeeNumber) ? 'Assigning...' : 'Assign'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <p>✅ No unassigned employees found!</p>
                    </div>
                  )}
                </div>
              )}

              {/* DUPLICATES TAB */}
              {activeTab === 'duplicates' && (
                <div className={styles.listView}>
                  <div className={styles.searchBar}>
                    <input
                      type="text"
                      placeholder="Search duplicate assignments..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.searchInput}
                    />
                  </div>

                  {filteredDuplicates.length > 0 ? (
                    <div className={styles.employeeList}>
                      {filteredDuplicates.map((item, index) => (
                        <div key={item.employee.employeeNumber} className={styles.employeeCard}>
                          <div className={styles.employeeIndex}>{index + 1}</div>
                          <div className={styles.employeeInfo}>
                            <div className={styles.employeeName}>{item.employee.name}</div>
                            <div className={styles.employeeDetails}>
                              <span className={styles.empCode}>{item.employee.employeeNumber}</span>
                              {item.employee.designation && (
                                <span className={styles.designation}>{item.employee.designation}</span>
                              )}
                            </div>
                            <div className={styles.projectBadges}>
                              {item.projects.map(project => (
                                <span key={project} className={styles.projectBadge}>
                                  {project}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <p>✅ No duplicate assignments found!</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button onClick={loadAnalytics} className={styles.refreshButton}>
            🔄 Refresh
          </button>
          <button onClick={onClose} className={styles.closeButtonFooter}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManpowerSettingsModal;